import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import 'firebase/compat/database';
import 'firebase/compat/functions';
import 'firebase/compat/storage';
import {
  FIREBASE_EMULATOR_DEFAULT_HOST,
  FIREBASE_EMULATOR_DEFAULT_PORTS,
} from '@cip/test-support';
import {getAnalytics, type Analytics} from 'firebase/analytics';
import {connectAuthEmulator, getAuth} from 'firebase/auth';
import {
  connectDatabaseEmulator,
  getDatabase,
} from 'firebase/database';
import {connectFunctionsEmulator, getFunctions} from 'firebase/functions';
import {
  connectFirestoreEmulator,
  getFirestore,
  initializeFirestore,
  setLogLevel,
} from 'firebase/firestore';
import {isFirestoreDebugLoggingRequested} from './firestoreDebugLogging';
import {getApp} from 'firebase/app';
import {connectStorageEmulator, getStorage} from 'firebase/storage';
import {firebaseConfig} from './config';
import {
  getWebappBrowserTestQueryState,
  shouldHonorWebappBrowserTestQueryContract,
} from './browserTestHarness';
// Imported from `@cip/core/firebase` so every consumer in the monorepo
// (webapp, healthcheck, smoke page, code-runner cacheState, tests)
// agrees on the region and URL shape without redeclaring them.
import {FUNCTIONS_REGION, getFunctionsBaseUrl} from '@cip/core/firebase';

const app = firebase.initializeApp(firebaseConfig);

// Scoped to WebKit — macOS Safari plus every iOS browser, since Apple
// mandates WebKit there. Those clients stall ~30s on the streaming handshake;
// Blink/Gecko desktop has no reported instances and keeps streaming.
// UA test per https://rnwest.engineer/detect-webkit/: Chromium desktop is the
// only engine claiming AppleWebKit without being WebKit, and the iPhone/iPad
// clause is needed because iOS Chrome reports CriOS rather than Chrome. The
// WebKit-only `GestureEvent` is a second signal in case UA reduction ever
// erases the first — detection errs toward enabling, since a false positive
// costs some streaming efficiency while a false negative leaves the stall in
// place. Exposed on `window` so the decision can be confirmed per browser on
// a deployed build.
//
// Forced rather than left to `experimentalAutoDetectLongPolling` (the SDK
// default), which does not help — it detects a buffering proxy, and the
// affected environments pass Firebase's own transport diagnostic. Passed only
// when true because it cannot be combined with auto-detect; an empty settings
// object is equivalent to plain `getFirestore()`.
// Must precede any getFirestore()/firebase.firestore() call — the SDK throws
// once Firestore has started. See
// [#3697](https://github.com/CodeinPlace/cip/issues/3697).
const firestoreClientUserAgent =
  typeof navigator === 'undefined' ? '' : navigator.userAgent;
const firestoreUsesLongPolling =
  (/AppleWebKit/.test(firestoreClientUserAgent) &&
    !/Chrome/.test(firestoreClientUserAgent)) ||
  /\b(iPad|iPhone|iPod)\b/.test(firestoreClientUserAgent) ||
  (typeof window !== 'undefined' && 'GestureEvent' in window);
if (typeof window !== 'undefined') {
  window.__cipFirestoreLongPolling = firestoreUsesLongPolling;
}
// Before initializeFirestore so the SDK's own startup — transport selection,
// the first target registrations — is in the log, not just what follows it.
if (isFirestoreDebugLoggingRequested()) {
  setLogLevel('debug');
}
initializeFirestore(
  getApp(),
  firestoreUsesLongPolling ? {experimentalForceLongPolling: true} : {},
);
const EMULATOR_HOST =
  import.meta.env.VITE_FIREBASE_EMULATOR_HOST ||
  FIREBASE_EMULATOR_DEFAULT_HOST;
const AUTH_EMULATOR_PORT = Number(
  import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_PORT ||
    String(FIREBASE_EMULATOR_DEFAULT_PORTS.auth),
);
const FIRESTORE_EMULATOR_PORT = Number(
  import.meta.env.VITE_FIRESTORE_EMULATOR_PORT ||
    String(FIREBASE_EMULATOR_DEFAULT_PORTS.firestore),
);
const FUNCTIONS_EMULATOR_PORT = Number(
  import.meta.env.VITE_FUNCTIONS_EMULATOR_PORT ||
    String(FIREBASE_EMULATOR_DEFAULT_PORTS.functions),
);
const STORAGE_EMULATOR_PORT = Number(
  import.meta.env.VITE_FIREBASE_STORAGE_EMULATOR_PORT ||
    String(FIREBASE_EMULATOR_DEFAULT_PORTS.storage),
);
const DATABASE_EMULATOR_PORT = Number(
  import.meta.env.VITE_FIREBASE_DATABASE_EMULATOR_PORT ||
    String(FIREBASE_EMULATOR_DEFAULT_PORTS.database),
);
const AUTH_EMULATOR_URL = `http://${EMULATOR_HOST}:${AUTH_EMULATOR_PORT}`;
// Which worktree's emulators this dev server is wired to, set by
// `mise start:emulators` (see start-emulators.sh). Used only to label the
// on-page emulator-mode banner so a dev running several parallel stacks
// (typically one per worktree) can tell them apart. Undefined outside that
// flow (e.g. browser tests).
const EMULATOR_WORKTREE = import.meta.env.VITE_FIREBASE_EMULATOR_WORKTREE;
// Keep the webapp's supported emulator service ids in one const-derived list
// so the runtime guard, connection logic, and test helpers stay in sync.
const SUPPORTED_FIREBASE_EMULATOR_SERVICES = [
  'auth',
  'firestore',
  'functions',
  'storage',
  'database',
] as const;
type FirebaseEmulatorServiceId =
  (typeof SUPPORTED_FIREBASE_EMULATOR_SERVICES)[number];
const SUPPORTED_FIREBASE_EMULATOR_SERVICE_SET = new Set<string>(
  SUPPORTED_FIREBASE_EMULATOR_SERVICES,
);

/** Browser-local record of Firebase emulator services already connected. */
interface FirebaseEmulatorConnectionState {
  auth: boolean;
  firestore: boolean;
  functions: boolean;
  storage: boolean;
  database: boolean;
}

/** Runtime summary of one Firebase service's emulator target. */
export interface FirebaseEmulatorServiceTarget {
  available: boolean;
  connected: boolean;
  host: string;
  port: number;
  requested: boolean;
  target: string;
}

/** Runtime summary of the Firebase emulator configuration used by the webapp. */
export interface FirebaseEmulatorRuntimeState {
  auth: FirebaseEmulatorServiceTarget;
  firestore: FirebaseEmulatorServiceTarget;
  functions: FirebaseEmulatorServiceTarget;
  storage: FirebaseEmulatorServiceTarget;
  database: FirebaseEmulatorServiceTarget;
  projectId: string;
}

function isFirebaseEmulatorServiceId(
  service: string,
): service is FirebaseEmulatorServiceId {
  return SUPPORTED_FIREBASE_EMULATOR_SERVICE_SET.has(service);
}

function createFirebaseEmulatorConnectionState(): FirebaseEmulatorConnectionState {
  return {
    auth: false,
    firestore: false,
    functions: false,
    storage: false,
    database: false,
  };
}

function readFirebaseEmulatorConnectionState(): FirebaseEmulatorConnectionState {
  if (typeof window === 'undefined') {
    return createFirebaseEmulatorConnectionState();
  }

  return (
    window.__cipFirebaseEmulatorConnections ??
    createFirebaseEmulatorConnectionState()
  );
}

function getFirebaseEmulatorConnectionState(): FirebaseEmulatorConnectionState {
  if (typeof window === 'undefined') {
    return createFirebaseEmulatorConnectionState();
  }

  window.__cipFirebaseEmulatorConnections ??=
    createFirebaseEmulatorConnectionState();
  return window.__cipFirebaseEmulatorConnections;
}

/**
 * Services declared via the dev-mode env var
 * `VITE_FIREBASE_EMULATOR_SERVICES`, parsed once at module load.
 *
 * This is the dev-time path used by `mise start:emulators` (see
 * `start-emulators.sh`). The script sets the env var to the comma-
 * separated list of services it actually started, and every page load
 * connects to those emulators unconditionally — no `?firebase=...`
 * query string required.
 *
 * The browser-test capability query contract
 * (`VITE_BROWSER_TEST_MODE=true` + `?firebase=...`) is a separate path
 * for playwright suites that opt in per-test; see
 * {@link getRequestedFirebaseEmulatorServices}.
 */
const ENV_REQUESTED_FIREBASE_EMULATOR_SERVICES: ReadonlySet<FirebaseEmulatorServiceId> =
  (() => {
    const raw = import.meta.env.VITE_FIREBASE_EMULATOR_SERVICES;
    if (typeof raw !== 'string' || raw.length === 0) {
      return new Set<FirebaseEmulatorServiceId>();
    }
    return new Set(
      raw
        .split(',')
        .map(token => token.trim())
        .filter(isFirebaseEmulatorServiceId),
    );
  })();

function getRequestedFirebaseEmulatorServices(): Set<FirebaseEmulatorServiceId> {
  if (shouldHonorWebappBrowserTestQueryContract()) {
    return new Set(
      getWebappBrowserTestQueryState().resolvedPlan?.firebase.filter(
        isFirebaseEmulatorServiceId,
      ) ?? [],
    );
  }

  return new Set(ENV_REQUESTED_FIREBASE_EMULATOR_SERVICES);
}

const REQUESTED_FIREBASE_EMULATOR_SERVICES =
  getRequestedFirebaseEmulatorServices();

function isFirebaseEmulatorAvailable(): boolean {
  return (
    shouldHonorWebappBrowserTestQueryContract() ||
    ENV_REQUESTED_FIREBASE_EMULATOR_SERVICES.size > 0
  );
}

function createFirebaseEmulatorServiceTarget(
  serviceId: FirebaseEmulatorServiceId,
  port: number,
  target: string,
): FirebaseEmulatorServiceTarget {
  const emulatorConnections = readFirebaseEmulatorConnectionState();

  return {
    available: isFirebaseEmulatorAvailable(),
    connected: emulatorConnections[serviceId],
    host: EMULATOR_HOST,
    port,
    requested: REQUESTED_FIREBASE_EMULATOR_SERVICES.has(serviceId),
    target,
  };
}

/**
 * Swallows the error thrown when a Firebase service has already been
 * pointed at an emulator, and rethrows anything else. Used by every
 * `connectXEmulatorServices` helper to make repeated calls (e.g. across
 * Vite HMR boundaries or duplicated module evaluation) idempotent.
 *
 * Each Firebase SDK shape produces a slightly different error message
 * for the duplicate-configuration case ("already called", "already
 * configured", "already initialized"), so the regex covers all three
 * known wordings.
 *
 * @param error - The thrown value from a `useEmulator` / `connectXEmulator`
 *   call. Typed as `unknown` because Firebase's emulator helpers vary in
 *   what shape they reject with.
 * @throws Whatever was passed in, unless the error matches the
 *   duplicate-configuration regex.
 */
function ignoreDuplicateEmulatorConfiguration(error: unknown): void {
  if (
    error instanceof Error &&
    /already (?:been )?called|already configured|already initialized/i.test(
      error.message,
    )
  ) {
    return;
  }

  throw error;
}

// ===========================================================================
// Emulator-connect helpers
//
// Every `connectXEmulatorServices` function in this file follows the same
// three-step shape:
//
//   1. **Gate**: return immediately if the emulator service was not
//      requested via the browser-test capability query, OR if it has already been
//      connected on this page (the connection state is keyed on `window`
//      so it survives Vite HMR rebuilds).
//
//   2. **Dual-API wiring**: call BOTH `firebase.X().useEmulator(...)`
//      (compat API, used by older webapp code paths that haven't migrated
//      to the modular Firebase v9+ API) AND `connectXEmulator(getX(), ...)`
//      (modular API, used by everything new). Each call is wrapped in
//      `ignoreDuplicateEmulatorConfiguration` so a re-evaluation of this
//      module after the first connection is a no-op rather than a throw.
//
//      The dual-call pattern is intentional belt-and-braces: the webapp
//      mixes compat and modular Firebase SDK imports across a multi-year
//      migration, and at any given moment some component may be reading
//      via one API while another reads via the other. Connecting both
//      keeps both views of the SDK pointing at the emulator.
//
//   3. **Mark**: flip the per-service flag on the window-level connection
//      state so subsequent calls take the early-return.
//
// New emulator services should follow the same shape so behavior stays
// uniform and future readers can scan one helper to understand all of
// them.
// ===========================================================================

/**
 * Wires the Firebase Auth emulator, both via the compat and modular SDK
 * APIs. No-op when `auth` was not requested in the browser-test capability query
 * or when the connection is already established. See the section comment
 * above this helper for the shared three-step pattern.
 */
function connectAuthEmulatorServices(): void {
  const emulatorConnections = getFirebaseEmulatorConnectionState();
  if (
    !REQUESTED_FIREBASE_EMULATOR_SERVICES.has('auth') ||
    emulatorConnections.auth
  ) {
    return;
  }

  try {
    firebase.auth().useEmulator(AUTH_EMULATOR_URL);
  } catch (error) {
    ignoreDuplicateEmulatorConfiguration(error);
  }

  try {
    connectAuthEmulator(getAuth(), AUTH_EMULATOR_URL, {
      disableWarnings: true,
    });
  } catch (error) {
    ignoreDuplicateEmulatorConfiguration(error);
  }

  emulatorConnections.auth = true;
}

/**
 * Wires the Firestore emulator, both via the compat and modular SDK APIs.
 * No-op when `firestore` was not requested in the browser-test capability query or
 * when the connection is already established. See {@link
 * connectAuthEmulatorServices} for the shared three-step pattern.
 */
function connectFirestoreEmulatorServices(): void {
  const emulatorConnections = getFirebaseEmulatorConnectionState();
  if (
    !REQUESTED_FIREBASE_EMULATOR_SERVICES.has('firestore') ||
    emulatorConnections.firestore
  ) {
    return;
  }

  try {
    firebase.firestore().useEmulator(EMULATOR_HOST, FIRESTORE_EMULATOR_PORT);
  } catch (error) {
    ignoreDuplicateEmulatorConfiguration(error);
  }

  try {
    connectFirestoreEmulator(
      getFirestore(),
      EMULATOR_HOST,
      FIRESTORE_EMULATOR_PORT,
    );
  } catch (error) {
    ignoreDuplicateEmulatorConfiguration(error);
  }

  emulatorConnections.firestore = true;
}

/**
 * Wires the Firebase Functions emulator, both via the compat and modular
 * SDK APIs. No-op when `functions` was not requested in the browser-test
 * capability query or when the connection is already established. See
 * {@link connectAuthEmulatorServices} for the shared three-step pattern.
 */
function connectFunctionsEmulatorServices(): void {
  const emulatorConnections = getFirebaseEmulatorConnectionState();
  if (
    !REQUESTED_FIREBASE_EMULATOR_SERVICES.has('functions') ||
    emulatorConnections.functions
  ) {
    return;
  }

  try {
    firebase
      .app()
      .functions(FUNCTIONS_REGION)
      .useEmulator(EMULATOR_HOST, FUNCTIONS_EMULATOR_PORT);
  } catch (error) {
    ignoreDuplicateEmulatorConfiguration(error);
  }

  try {
    connectFunctionsEmulator(
      getFunctions(app, FUNCTIONS_REGION),
      EMULATOR_HOST,
      FUNCTIONS_EMULATOR_PORT,
    );
  } catch (error) {
    ignoreDuplicateEmulatorConfiguration(error);
  }

  emulatorConnections.functions = true;
}

/**
 * Wires the Firebase Storage emulator, both via the compat and modular
 * SDK APIs. No-op when `storage` was not requested in the browser-test capability
 * query or when the connection is already established. See {@link
 * connectAuthEmulatorServices} for the shared three-step pattern.
 */
function connectStorageEmulatorServices(): void {
  const emulatorConnections = getFirebaseEmulatorConnectionState();
  if (
    !REQUESTED_FIREBASE_EMULATOR_SERVICES.has('storage') ||
    emulatorConnections.storage
  ) {
    return;
  }

  try {
    firebase.storage().useEmulator(EMULATOR_HOST, STORAGE_EMULATOR_PORT);
  } catch (error) {
    ignoreDuplicateEmulatorConfiguration(error);
  }

  try {
    connectStorageEmulator(getStorage(), EMULATOR_HOST, STORAGE_EMULATOR_PORT);
  } catch (error) {
    ignoreDuplicateEmulatorConfiguration(error);
  }

  emulatorConnections.storage = true;
}

/**
 * Wires the Realtime Database emulator, both via the compat and modular
 * SDK APIs. No-op when `database` was not requested in the browser-test capability
 * query or when the connection is already established. See {@link
 * connectAuthEmulatorServices} for the shared three-step pattern.
 */
function connectDatabaseEmulatorServices(): void {
  const emulatorConnections = getFirebaseEmulatorConnectionState();
  if (
    !REQUESTED_FIREBASE_EMULATOR_SERVICES.has('database') ||
    emulatorConnections.database
  ) {
    return;
  }

  try {
    firebase.database().useEmulator(EMULATOR_HOST, DATABASE_EMULATOR_PORT);
  } catch (error) {
    ignoreDuplicateEmulatorConfiguration(error);
  }

  try {
    connectDatabaseEmulator(
      getDatabase(app),
      EMULATOR_HOST,
      DATABASE_EMULATOR_PORT,
    );
  } catch (error) {
    ignoreDuplicateEmulatorConfiguration(error);
  }

  emulatorConnections.database = true;
}

/**
 * Top-level entry point: wires every emulator service the webapp
 * advertises support for. No-op in non-browser environments (e.g. SSR,
 * Vite's pre-bundling pass) where `window` is undefined. Individual
 * `connectXEmulatorServices` helpers handle their own gating, so this
 * function is just a fan-out.
 */
function connectFirebaseEmulators(): void {
  if (typeof window === 'undefined') {
    return;
  }

  connectAuthEmulatorServices();
  connectFirestoreEmulatorServices();
  connectFunctionsEmulatorServices();
  connectStorageEmulatorServices();
  connectDatabaseEmulatorServices();
}

/**
 * Appends the worktree name (from `VITE_FIREBASE_EMULATOR_WORKTREE`) to the
 * Firebase Auth SDK's
 * "Running in emulator mode. Do not use with production credentials." banner,
 * so a developer running several parallel emulator stacks side by side
 * (typically one per worktree) can tell which one a tab is talking to.
 *
 * The SDK injects that banner as `<div id="firebase-emulator-warning">` when
 * the Auth compat SDK connects to the emulator — but only once the DOM is
 * ready, which may be after this module evaluates. So this tries once
 * immediately and, failing that, watches the DOM until the banner appears (or a
 * short safety timeout elapses), then annotates it exactly once.
 *
 * No-op when `VITE_FIREBASE_EMULATOR_WORKTREE` is unset (any non-emulator run),
 * when the Auth emulator banner never appears (auth not connected), or outside
 * a browser (`document` undefined).
 *
 * @throws Never.
 */
function annotateEmulatorWorktreeBanner(): void {
  if (typeof document === 'undefined' || !EMULATOR_WORKTREE) {
    return;
  }
  const BANNER_ID = 'firebase-emulator-warning';
  const MARKER_ATTR = 'data-cip-emulator-worktree';
  const applyLabel = (): boolean => {
    const banner = document.getElementById(BANNER_ID);
    if (!banner) {
      return false;
    }
    if (banner.querySelector(`[${MARKER_ATTR}]`)) {
      return true;
    }
    const label = document.createElement('span');
    label.setAttribute(MARKER_ATTR, '');
    label.textContent = ` — worktree: ${EMULATOR_WORKTREE}`;
    banner.appendChild(label);
    return true;
  };

  if (applyLabel()) {
    return;
  }

  const observer = new MutationObserver(() => {
    if (applyLabel()) {
      observer.disconnect();
    }
  });
  const startObserving = (): void => {
    observer.observe(document.body, {childList: true, subtree: true});
    // Safety valve: stop watching after a few seconds so the observer never
    // lingers when the banner is never created (e.g. auth emulator not on).
    const OBSERVER_TIMEOUT_MS = 5000;
    window.setTimeout(() => observer.disconnect(), OBSERVER_TIMEOUT_MS);
  };
  if (document.body) {
    startObserving();
  } else {
    document.addEventListener('DOMContentLoaded', startObserving, {once: true});
  }
}

connectFirebaseEmulators();
annotateEmulatorWorktreeBanner();

/**
 * Returns the Firebase emulator runtime configuration that the webapp is
 * using for Auth, Firestore, Functions, Storage, and Realtime Database.
 */
export function getFirebaseEmulatorRuntimeState(): FirebaseEmulatorRuntimeState {
  return {
    auth: createFirebaseEmulatorServiceTarget(
      'auth',
      AUTH_EMULATOR_PORT,
      AUTH_EMULATOR_URL,
    ),
    firestore: createFirebaseEmulatorServiceTarget(
      'firestore',
      FIRESTORE_EMULATOR_PORT,
      `${EMULATOR_HOST}:${FIRESTORE_EMULATOR_PORT}`,
    ),
    functions: createFirebaseEmulatorServiceTarget(
      'functions',
      FUNCTIONS_EMULATOR_PORT,
      getFunctionsBaseUrl({
        projectId: firebaseConfig.projectId,
        emulator: {host: EMULATOR_HOST, port: FUNCTIONS_EMULATOR_PORT},
      }),
    ),
    storage: createFirebaseEmulatorServiceTarget(
      'storage',
      STORAGE_EMULATOR_PORT,
      `${EMULATOR_HOST}:${STORAGE_EMULATOR_PORT}`,
    ),
    database: createFirebaseEmulatorServiceTarget(
      'database',
      DATABASE_EMULATOR_PORT,
      `${EMULATOR_HOST}:${DATABASE_EMULATOR_PORT}`,
    ),
    projectId: firebaseConfig.projectId,
  };
}

/** Firestore instance used throughout the webapp. */
export const firestore = firebase.firestore();

/** Realtime Database instance used throughout the webapp. */
export const database = firebase.database();

/** Cloud Functions instance targeting the `us-central1` region. */
export const functions = firebase.app().functions(FUNCTIONS_REGION);

/** Browser-local cached Firebase Analytics instance. */
let analyticsInstance: Analytics | null | undefined;

/**
 * Return the Firebase Analytics instance when available.
 *
 * Analytics is non-critical; callers must tolerate `null` so public routes do
 * not fail when Firebase Installations is unavailable.
 */
export function getOptionalAnalytics(): Analytics | null {
  if (analyticsInstance !== undefined) {
    return analyticsInstance;
  }
  if (typeof window === 'undefined') {
    analyticsInstance = null;
    return analyticsInstance;
  }
  try {
    analyticsInstance = getAnalytics(app);
  } catch (error) {
    analyticsInstance = null;
    console.debug('Firebase Analytics unavailable', error);
  }
  return analyticsInstance;
}
